import React, { useRef, useMemo, useEffect, useLayoutEffect, useContext, useState, memo } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { RootState } from 'store';
import { addData } from 'store/secondaryVideoTabsDataSlice';
import { fetchVideoChapters } from 'api/videosAPI';
// import { updateTabContent, getScrollPosition, seekPlayerTime } from 'store/secondaryViewSlice';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { ModalContext } from 'context/ModalContext';
import EncytedImage from './_components/EncytedImage';

import ReactMarkdown from 'react-markdown';
import clsx from 'clsx';
import { isMobile } from 'react-device-detect';
import 'react-image-lightbox/style.css';
import Lightbox from 'react-image-lightbox';
import { decryptImageFromUrl } from 'hooks/useImageDecrypt';

import { updateTabContent, getScrollPosition, seekPlayerTime } from 'store/secondaryChannelVideoSlice';

interface IChapters {
  currentVideoShot: number;
  channelId: string;
  videoHashId: string;
  selected: number;
  videoShots: any;
  navigateVideoPage: any;
  secondaryViewSlice: any;
  decryptionKeys: any;
  aesKeyId: string;
}

function ChapterView({
  currentVideoShot,
  channelId,
  videoHashId,
  selected,
  videoShots,
  navigateVideoPage,
  secondaryViewSlice,
  decryptionKeys,
  aesKeyId
}: IChapters) {
  const [hovered, setHovered] = useState(false);
  const [openLightbox, setOpenLightbox] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [largeImage, setLargeImage] = useState<string | null>('');
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [chapterItem, setChapterItem] = useState(null);
  const secondaryViewWidth = useSelector((state: RootState) => state.app.secondaryViewWidth);

  const scrollRef = useRef<HTMLElement>();
  const keyId = channelId + videoHashId + currentVideoShot;
  const dispatch = useDispatch();
  const scrolled = useRef(false);
  const { showModal } = useContext(ModalContext);
  // const [decryptedData, setDecrytedData] = useState(null as any);
  // const encryptedThumbnailsState = useSelector((state: RootState) => state.decryptedImages);

  const videoShotId = useMemo(() => {
    if (!videoShots?.length) return;
    const idx = videoShots[currentVideoShot]?.shot_hash_id;
    return idx;
  }, [keyId, videoHashId, channelId, videoShots, currentVideoShot]);

  const chapterState = useSelector(
    (state: RootState) => state.secondaryViewTab.videos.find((video) => video.videoShotId === videoShotId)?.chapters
  );

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (!videoShotId) return;

      if (mounted) {
        const response = await fetchVideoChapters({
          channelHashId: channelId,
          videoHashId: videoHashId,
          shotId: videoShotId
        });
        dispatch(addData({ videoHashId: keyId, videoShotId: videoShotId, key: 'chapters', data: response.data }));
      }
    };

    if (!chapterState) {
      fetchData();
    }
    return () => {
      mounted = false;
    };
  }, [channelId, videoShotId]);

  const getNamesString = (members: any) => {
    if (!members.length) return;
    return members.map((member: any) => member.name).join(', ');
  };

  const cleanTitle = (title: string) => {
    if (!title?.length) return;
    return title.split('"').join('');
  };

  const formatTime = (time: number) => {
    return time
      ? time > 3600
        ? moment.duration(time, 'seconds').format('h:mm:ss', { trim: false })
        : moment.duration(time, 'seconds').format('m:ss', { trim: false })
      : '';
  };

  const dispatchUpdatedContent = (currentPosition: number) => {
    if (currentPosition !== undefined) {
      dispatch(
        updateTabContent({
          channelHashId: channelId,
          videoHashId: videoHashId,
          data: {
            chapters: { scrollPosition: currentPosition }
          }
        })
      );
    }
  };

  const timer = useRef<NodeJS.Timeout | null>(null); // Explicitly type the ref
  const handleScroll = () => {
    scrolled.current = true;
    if (timer.current) {
      clearTimeout(timer.current);
    }
    // Set a timeout to detect when scrolling has stopped
    timer.current = setTimeout(() => {
      if (scrollRef.current) {
        console.log('saving scroll chapter', channelId, scrollRef.current.scrollTop);
        dispatchUpdatedContent(scrollRef.current.scrollTop);
      }
    }, 200); // 200ms del
  };

  // useLayoutEffect(() => {
  //   // save scroll position when unmount
  //   return () => {
  //     if (selected === 1) {
  //       if (scrollRef.current && scrolled.current) {
  //         dispatchUpdatedContent(scrollRef.current.scrollTop);
  //       }
  //     }
  //   };
  // }, [keyId, selected]);

  // useEffect(() => {
  //   console.log('scrolling');
  //   if (scrollRef.current && scrolled.current) {
  //     console.log('set scoll scrolling', scrollRef.current.scrollTop);
  //     dispatchUpdatedContent(scrollRef.current.scrollTop);
  //   }
  // }, [scrolled.current, scrollRef.current]);

  const setEnlargeImage = (isMounted: boolean, chapter: any) => {
    if (aesKeyId && decryptionKeys) {
      setImageLoading(true);
      const { key, iv } = decryptionKeys;
      if (isMounted) {
        decryptImageFromUrl(chapter.keyframe?.image, key, iv).then((decryptedUrl) => {
          setLargeImage(decryptedUrl);
          setImageLoading(false);
          setLoading(false);
        });
      }
    } else {
      if (chapter.keyframe?.image) {
        setLargeImage(chapter.keyframe?.image);
        setImageLoading(false);
        setLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   return () => {
  //     setLoading(true);
  //   };
  // }, [openLightbox]);

  useLayoutEffect(() => {
    const defaultScrollTop = getScrollPosition(secondaryViewSlice, channelId, videoHashId, 'chapters');
    console.log('defaultScrollTop chapter', videoHashId, defaultScrollTop);
    if (scrollRef.current && defaultScrollTop !== undefined) {
      scrollRef.current.classList.add(`opacity-0`);
    } else if (scrollRef.current && defaultScrollTop === undefined) {
      scrollRef.current.scrollTop = 0;
    }

    setTimeout(() => {
      if (scrollRef && scrollRef.current && defaultScrollTop !== undefined) {
        scrollRef.current.classList.add(`transition-opacity`, `duration-200`, `ease-in-out`, `opacity-100`);
        scrollRef.current.scrollTop = defaultScrollTop;
      }
    }, 100);

    return () => {
      if (timer.current) {
        clearTimeout(timer.current); // Cleanup the timeout on unmount
      }
    };
  }, [videoHashId, selected]);

  const handleOpenSummary = (topics: any[], chapter: string) => {
    showModal(MODAL_TYPES.chapter_summary, {
      animation: MODAL_ANIMATIONS.fade,
      modalMessage: {
        title: 'Summary'
      },
      chapterTitle: chapter,
      topics: topics
    });
  };

  const navigateToVideo = (time: any) => {
    const isActivePlayer = sessionStorage.getItem('playerActive');
    if (isActivePlayer === 'true') {
      dispatch(seekPlayerTime({ channelHashId: channelId, time: time }));
    } else {
      navigateVideoPage(time);
    }
  };

  return (
    <div className={`absolute w-full flex flex-col flex-1 top-0 right-0 left-0 bottom-0 overflow-y-hidden text-sm`}>
      <PerfectScrollbar
        containerRef={(el) => (scrollRef.current = el)}
        style={{ maxHeight: `100%` }}
        options={{ suppressScrollY: false, suppressScrollX: true }}
        onScrollY={handleScroll}
      >
        <div className="px-4 mt-[4px]">
          {chapterState?.map((chapter: any, index: number) => (
            <div className="segments" key={index}>
              <h2 className=" text-iconColor font-lato no-underline font-black text-[15px] leading-[1.4] break-words">
                {cleanTitle(chapter.title)}
              </h2>
              <div className="flex py-2">
                {chapter.speakers?.slice(0, 5).map((speaker: any, index: number) => (
                  <div
                    className={`cursor-pointer mt-1 w-6 h-6 z-[${10 - index}] ${index > 0 ? '-ml-2' : ''}`}
                    key={index}
                  >
                    <LazyLoadImage
                      className={`rounded-full w-6 h-6`}
                      width={24}
                      height={24}
                      src={speaker.avatar ? speaker.avatar : '/assets/img/no-img.jpg'}
                      alt={`${speaker.name} - Image`}
                      key={'avatar' + index}
                    />
                  </div>
                ))}
                <div className="ml-2 text-[13px] leading-[1.2]">
                  <div className="truncate font-bold items-center max-w-xs">
                    <span>{getNamesString(chapter.speakers)}</span>
                  </div>
                  <span className="cursor-pointer text-[#1664A3]" onClick={() => navigateToVideo(chapter.start_time)}>
                    {chapter.start_time === 0 ? '0:00' : formatTime(chapter.start_time)}
                  </span>
                </div>
              </div>
              <div className="chapter-markdown font-lato text-sm">
                {chapter.summary ? (
                  <ReactMarkdown>{chapter.summary}</ReactMarkdown>
                ) : (
                  <ReactMarkdown>
                    {chapter.summary_markdown?.replace(/```markdown\n/, '').replace(/\n```$/, '')}
                  </ReactMarkdown>
                )}
              </div>
              {Array.isArray(chapter.topics) && chapter.topics.filter(Boolean).length > 0 && (
                <div className="pt-1">
                  <h2
                    className="text-sm font-bold cursor-pointer text-[#1664A3]"
                    onClick={() => {
                      handleOpenSummary(chapter.topics, chapter.title);
                    }}
                  >
                    Topics
                  </h2>
                </div>
              )}
              {chapter.keyframe?.image && (
                <div
                  className="pt-1 relative bg-transparent"
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  onTouchStart={() => setHovered(!hovered)}
                >
                  <div>
                    {chapter.keyframe?.title && <h2 className="text-sm font-bold">{chapter.keyframe?.title}</h2>}
                    {decryptionKeys ? (
                      <div
                        className="pt-2 cursor-pointer"
                        onClick={() => {
                          navigateToVideo(chapter.start_time);
                        }}
                      >
                        <EncytedImage
                          videoHashId={videoHashId}
                          encryptedImageUrl={chapter.keyframe.image}
                          decryptionKeys={decryptionKeys}
                          navigateToVideo={navigateToVideo}
                          timestamp={chapter.start_time}
                          secondaryViewSize={secondaryViewWidth}
                        />
                      </div>
                    ) : (
                      <img
                        src={chapter.keyframe?.image}
                        alt={`thumbnail for ${videoHashId}`}
                        className={`rounded-lg w-full object-fit max-w-[371px] min-h-[200px] h-full cursor-pointer`}
                        onClick={() => {
                          navigateToVideo(chapter.start_time);
                        }}
                      />
                    )}
                    <div
                      className={clsx(
                        'absolute text-white bg-[#515151] px-1 py-1 text-sm items-center justify-center rounded-md bottom-6 right-14 md:group-hover:flex cursor-pointer',
                        {
                          visible: hovered || isMobile,
                          invisible: !hovered && !isMobile
                        }
                      )}
                      onClick={() => {
                        setEnlargeImage(true, chapter);
                        setLargeImage(chapter.keyframe?.image);
                        setOpenLightbox(true);
                      }}
                    >
                      Click to enlarge
                    </div>
                    {openLightbox && largeImage && (
                      <Lightbox
                        mainSrc={largeImage}
                        onCloseRequest={() => setOpenLightbox(false)}
                        onImageLoad={() => setImageLoading(false)}
                        imageLoadErrorMessage={imageLoading ? '' : 'This image failed to load '}
                      />
                    )}
                  </div>
                </div>
              )}
              <div className="py-3">
                <div className="border-b border-line"></div>
              </div>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
    </div>
  );
}

export default memo(ChapterView);
